import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import {
    normalizeToProblemDetails,
    toProblemToastCopy,
} from '@quipex/core/utils';
import {
    BuildingsApiService,
    type DocumentActionContext,
    type PackActionProposal,
} from '@quipex/shared/contracts';
import { firstValueFrom } from 'rxjs';
import { QUIPPY_AUTH_IDENTITY } from '../session-store/session-store';
import { QUIPPY_PACK_ACTIONS_ENABLED } from './config';
import type {
    PackActionState,
    QuippyPackCommand,
    QuippyPackPreview,
} from './models';
import { selectedSourceContext } from './selection';

@Injectable()
export class QuippyPackActions {
    private readonly api = inject(BuildingsApiService);
    private readonly identity = inject(QUIPPY_AUTH_IDENTITY, {
        optional: true,
    });
    public readonly enabled = inject(QUIPPY_PACK_ACTIONS_ENABLED);
    private readonly states = signal<ReadonlyMap<string, PackActionState>>(
        new Map()
    );
    private generation = 0;
    constructor() {
        effect(() => {
            this.identity?.();
            this.generation++;
            untracked(() => this.states.set(new Map()));
        });
    }
    public selectedContext(
        context: DocumentActionContext
    ): DocumentActionContext {
        return selectedSourceContext(context, new Map());
    }
    public state(id: string): PackActionState | undefined {
        return this.states().get(id);
    }
    public proposal(id: string, proposal: PackActionProposal): void {
        if (this.enabled() && !this.state(id))
            this.set(id, { phase: 'needsSelection', proposal });
    }
    public edit(id: string, proposal: PackActionProposal): void {
        if (
            !['applying', 'uncertain', 'applied'].includes(
                this.state(id)?.phase ?? ''
            )
        )
            this.set(id, { phase: 'needsSelection', proposal });
    }
    public preview(
        id: string,
        proposal: PackActionProposal,
        command: QuippyPackCommand,
        buildingName = 'Selected building'
    ): void {
        if (
            !this.enabled() ||
            ['applying', 'uncertain', 'applied'].includes(
                this.state(id)?.phase ?? ''
            )
        )
            return;
        const titles = new Map(
            [...proposal.documents, ...(proposal.excludedDocuments ?? [])].map(
                (document) => [document.documentId, document.title]
            )
        );
        const preview: QuippyPackPreview = {
            command,
            buildingName,
            packName: command.name,
            rows: command.sections.flatMap((section) =>
                section.requirements.map((requirement) => ({
                    documents: (
                        requirement.assignedDocumentIds ??
                        (requirement.assignedDocumentId
                            ? [requirement.assignedDocumentId]
                            : [])
                    ).map((id) => ({
                        id,
                        name: titles.get(id) ?? requirement.name,
                    })),
                    sectionName: section.name,
                    requirementName: requirement.name,
                    description: requirement.description,
                }))
            ),
        };
        this.set(id, { phase: 'ready', proposal, preview });
    }
    public async apply(id: string): Promise<void> {
        const current = this.state(id);
        if (!this.enabled() || current?.phase !== 'ready') return;
        const generation = this.generation;
        this.set(id, { phase: 'applying', preview: current.preview });
        try {
            const { buildingId, ...request } = current.preview.command;
            const pack = await firstValueFrom(
                this.api.createBuildingDocumentPack(buildingId, request)
            );
            if (generation === this.generation)
                this.set(id, { phase: 'applied', pack });
        } catch (error) {
            if (generation !== this.generation) return;
            const problemDetails = normalizeToProblemDetails(error);
            if (
                [400, 401, 403, 404, 409, 422].includes(problemDetails.status)
            ) {
                const copy = toProblemToastCopy(problemDetails, {
                    defaultMessage:
                        'The pack could not be created. Check the selected documents and your access, then review again.',
                });
                this.set(id, {
                    phase: 'needsReview',
                    proposal: current.proposal,
                    message: [copy.message, copy.description]
                        .filter(Boolean)
                        .join('\n'),
                });
            } else
                this.set(id, {
                    phase: 'uncertain',
                    buildingId: current.preview.command.buildingId,
                    message:
                        'The connection was interrupted. The pack may have been created. Check Packs before trying again.',
                });
        }
    }
    private set(id: string, state: PackActionState): void {
        this.states.update((previous) => new Map(previous).set(id, state));
    }
}
